import React, { useMemo, useRef } from "react";
import { change, Field, formValueSelector } from "redux-form";
import { IconButton } from "@mui/material";
import { useDrag, useDrop } from 'react-dnd'
import { customTextarea } from "../../../common/customTextarea";

import styles from "../../v2Styles.module.css";
import { useDispatch, useSelector } from "react-redux";

const style = {
  borderTop: '1px solid #000 !important',
  borderColor: "#000 !important",
  backgroundColor: 'white',
};

export default function ConsequencesSection({ fields, loading, meta: { error }, hazIndex, actIndex, fieldName, disabled, formName }) {

  const dispatch = useDispatch();
  const selector = useMemo(() => formValueSelector(formName), [formName]);
  const consequences = useSelector((state) => selector(state, fieldName)) || [];

  const onMoveRow = (dragIndex, hoverIndex) => {
    if (dragIndex !== hoverIndex) {
      fields.move(dragIndex, hoverIndex);
      dispatch(change(formName, "_v", Date.now()))
    }
  }

  return (
    <div>
      {error ? <div className="error mt-2">{error}</div> : ""}
      {fields.map((member, index) => {
        const rowId = consequences && consequences[index] && consequences[index].id ? consequences[index].id : `${member}-fallback-${index}`;
        return <DraggableConsequence moveRow={onMoveRow} disabled={disabled} loading={loading} actIndex={actIndex} hazIndex={hazIndex} key={rowId} index={index} member={member} fields={fields} />
      }
      )}
      <div className="">
        <button disabled={loading || disabled} type="button" className={`${styles.PrimaryButton} ${styles.SmallButton} mt-1`} onClick={() => fields.push({ id: Date.now(), isNew: true })}>
          Add Consequence
        </button>
      </div>
    </div>
  )
}

const DraggableConsequence = ({ fields, index, member, actIndex, hazIndex, loading, moveRow, disabled }) => {
  const ref = useRef(null);

  const [{ handlerId }, drop] = useDrop({
    accept: `TABLEROW_${actIndex}_${hazIndex}`,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) {
        return
      }
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }
      moveRow(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })
  const [{ isDragging }, drag, preview] = useDrag({
    type: `TABLEROW_${actIndex}_${hazIndex}`,
    item: () => {
      return { index: index }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })
  const opacity = isDragging ? 0.3 : 1
  drag(drop(ref))
  return (
    <div className="container-fluid px-0" data-handler-id={handlerId} ref={preview} style={{ ...style, opacity }}>
      <div className="row">
        <div className="col-12 d-flex justify-content-between" style={{ gap: "10px" }}>
          <div style={{ flex: "1" }}>
            <div className={styles.Label}>Consequence</div>
            <Field
              name={`${member}.consequence_name`}
              className={`${styles.TextArea} ${styles.AutoHeight} ${styles.Rows2}`}
              disabled={loading || disabled}
              component={customTextarea}
              autoComplete="off"
            />
          </div>
          <div className="d-flex mt-1" style={{ "flexDirection": "column" }}>
            <IconButton disabled={loading || disabled || fields.length <= 1} onClick={(e) => { e.stopPropagation(); e.preventDefault(); fields.remove(index) }} className={`${styles.IconBtn} ${styles.DangerColor} p-1 mt-2`} style={{ height: "fit-content" }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" style={{ height: "16px", width: "16px" }}>
                <path fill="currentColor" d="M232.7 69.9L224 96L128 96C110.3 96 96 110.3 96 128C96 145.7 110.3 160 128 160L512 160C529.7 160 544 145.7 544 128C544 110.3 529.7 96 512 96L416 96L407.3 69.9C402.9 56.8 390.7 48 376.9 48L263.1 48C249.3 48 237.1 56.8 232.7 69.9zM512 208L128 208L149.1 531.1C150.7 556.4 171.7 576 197 576L443 576C468.3 576 489.3 556.4 490.9 531.1L512 208z" />
              </svg>
            </IconButton>
            <IconButton ref={ref} disabled={loading || disabled || fields.length <= 1} onClick={(e) => { e.stopPropagation(); e.preventDefault(); }} className={`${styles.IconBtn} ${styles.SecondaryColor} p-1 mt-2`} style={{ height: "fit-content", cursor: "move" }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" style={{ height: "16px", width: "16px" }}>
                <path fill="currentColor" d="M288 104C288 81.9 270.1 64 248 64L200 64C177.9 64 160 81.9 160 104L160 152C160 174.1 177.9 192 200 192L248 192C270.1 192 288 174.1 288 152L288 104zM288 296C288 273.9 270.1 256 248 256L200 256C177.9 256 160 273.9 160 296L160 344C160 366.1 177.9 384 200 384L248 384C270.1 384 288 366.1 288 344L288 296zM160 488L160 536C160 558.1 177.9 576 200 576L248 576C270.1 576 288 558.1 288 536L288 488C288 465.9 270.1 448 248 448L200 448C177.9 448 160 465.9 160 488zM480 104C480 81.9 462.1 64 440 64L392 64C369.9 64 352 81.9 352 104L352 152C352 174.1 369.9 192 392 192L440 192C462.1 192 480 174.1 480 152L480 104zM352 296L352 344C352 366.1 369.9 384 392 384L440 384C462.1 384 480 366.1 480 344L480 296C480 273.9 462.1 256 440 256L392 256C369.9 256 352 273.9 352 296zM480 488C480 465.9 462.1 448 440 448L392 448C369.9 448 352 465.9 352 488L352 536C352 558.1 369.9 576 392 576L440 576C462.1 576 480 558.1 480 536L480 488z" />
              </svg>
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  )

}